import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { IStockByWarehouse, IProductHandlingUnit } from 'src/app/features/produits/models/products.models';
import { IOrderLineAlert } from '../../../models/quotations.models';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { IPriceBookViewDTO } from 'src/app/features/pricebook/models/pricebook.model';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { ObjectCustomFieldsViewComponent } from "src/app/shared/components/object-custom-fields-view/object-custom-fields-view.component";
import { CopyableComponent } from 'src/app/ui/copyable/copyable.component';
import { IObjectCustomFields, IObjectCustomFieldsParams } from 'src/app/features/settings/models/settings.models';
import { CustomFieldVisibilityTypes, RelatedObjectsTypes } from 'src/app/features/settings/models/custom-fields.constants';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { GetObjectCustomFields } from 'src/app/features/settings/store/settings.actions';
import { UpdateQuotationLines } from '../../../store/quotations.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { ProductNotesDialogComponent, IProductNotesDialogResult } from '../../product-notes-dialog/product-notes-dialog.component';
import { AppState } from 'src/app/core/app.state';
import { handlingUnitBaseMultiplier } from 'src/app/shared/utils/handling-unit.utils';
interface Warehouse {
  stock_id: number;
  warehouse_id: number;
  name: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  is_primary: boolean;
  order_line_id: number;
  order_id: number;
  state: string;
}

@Component({
  selector: 'app-stock-fulfillment',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, ObjectCustomFieldsViewComponent, CopyableComponent],
  templateUrl: './stock-fulfillment.component.html',
  styleUrls: ['./stock-fulfillment.component.scss'],
})
export class StockFulfillmentComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() product: any = null;
  @Input() order: any = null;
  @Input() canEdit = true;
  @Input() currentLanguage: string = 'en';
  @Input() lineAlerts: IOrderLineAlert[] = [];
  @Output() reservationChanged = new EventEmitter<any>();
  @Output() closed = new EventEmitter<void>();
  objectCustomFields: IObjectCustomFields | null = null;
  objectCustomFieldsParams: IObjectCustomFieldsParams = {
    object_type: RelatedObjectsTypes.INVENTORY,
    record_id: 0,
    visibility: [CustomFieldVisibilityTypes.BACKOFFICE],
    include_definitions: true,
  };
  baseUrl = environment.BASE_URL_API;
  form!: FormGroup;
  localProduct: any = null;
  reservations: any[] = [];

  warehouses: Warehouse[] = [];
  allocatedQuantity: number = 0;
  handlingUnits: IProductHandlingUnit[] = [];

  destroy$ = new Subject<void>();

  confirmationInProgress = false;

  canEditOrder$ = this.store.select(selectCanDo('order', 'edit'));
  canEditOrder: boolean = false;

  constructor(private fb: FormBuilder, private store: Store<AppState>, public featureFlags: FeatureFlagService, private dialog: MatDialog) {}

  /** Notes are editable while the order itself is editable — independent of `canEdit` (stock/price allocation). */
  get canEditNotes(): boolean {
    if (this.order?.is_archived) return false;
    return ['draft', 'requested', 'sent', 'to_be_validated'].includes(this.order?.status ?? '');
  }

  openLineNotesDialog(): void {
    if (!this.localProduct) return;
    const editable = this.canEditNotes && this.canEditOrder;
    this.dialog
      .open(ProductNotesDialogComponent, {
        data: {
          productName: this.localProduct.name,
          note: this.localProduct.notes ?? '',
          editable,
        },
      })
      .afterClosed()
      .subscribe((result: IProductNotesDialogResult | undefined) => {
        if (!result || !editable) return;
        if ((this.localProduct.notes ?? '') === result.note) return;
        // Reflect locally for immediate feedback, then persist via the shared lines-update flow.
        this.localProduct.notes = result.note;
        if (!this.order) return;
        if (!this.localProduct.order_line_id) return;
        this.store.dispatch(
          UpdateQuotationLines({
            id: this.order.id,
            payload: { update: [{ id: this.localProduct.order_line_id, notes: result.note }] },
          })
        );
      });
  }

  ngOnInit() {
    this.store.select(state => state.settings.objectCustomFields).subscribe((objectCustomFields: IObjectCustomFields | null) => {
      if(objectCustomFields) {
        this.objectCustomFields = objectCustomFields;
      }
    });

    this.canEditOrder$.pipe(takeUntil(this.destroy$)).subscribe((canEdit) => (this.canEditOrder = canEdit));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['product'] && this.product) {
      this.initializeProductData();
      this.form.markAllAsTouched();
      if(this.canEdit && this.order.status === 'sale') {
        this.canEdit = false;
      }
    }
  }

  get stock(): number {
    return typeof this.localProduct.stock === 'object' ? this.localProduct.stock?.quantity : this.localProduct.stock || 0;
  }

  private initializeProductData() {
    this.store.dispatch(GetObjectCustomFields({ params: {
      ...this.objectCustomFieldsParams,
      record_id: this.product.product_template_id ?? this.product.id ?? this.product.product_id,
    } }));
    
    // Deep clone the product to avoid mutating the original object
    this.localProduct = JSON.parse(JSON.stringify(this.product));

    if(this.localProduct.is_buy_x_get_y_line) return;

    // Initialize base form
    this.initializeForm();

    // Initialize the multi warehouse management
    this.initializeMultiWarehouseManagement();

    // Initialize the handling units management
    this.initializeHandlingUnitsManagement();
  }

  initializeForm() {
    this.form = this.fb.group({
      discount: new FormControl(this.localProduct.discount, [Validators.required, Validators.min(0), Validators.max(100)]),
      extra_discount: new FormControl(this.localProduct.extra_discount, [Validators.required, Validators.min(0), Validators.max(100)]),
      final_price: new FormControl(this.localProduct.final_price, [
        Validators.required,
        Validators.min(0),
        Validators.max(this.localProduct.price_unit),
      ]),
      order_currency: new FormControl(this.order.currency, []),
    });
  }

  initializeMultiWarehouseManagement() {
    if(!this.isMultiWarehouseStock()) return;
    // Initialize the warehouses and reservations

    // Deep clone the reservations
    this.reservations = JSON.parse(JSON.stringify(this.localProduct.stock_reservation || [])).map((reservation: any) => ({
      ...reservation,
    }));

    // Deep clone the warehouses
    this.warehouses = (this.localProduct.stock_by_warehouse || []).map((warehouse: any) => {
      return {
        ...warehouse,
        available_quantity: warehouse.available_quantity + this.reservedQuantityOnWarehouse(warehouse.warehouse_id),
      };
    });

    // Initialize the order allocations for each warehouse
    this.warehouses?.forEach((warehouse: any) => {
      let availableQuantity = warehouse.available_quantity;
      let orderAllocation = this.reservations?.find((stock: any) => stock.warehouse_id === warehouse.warehouse_id)?.quantity || 0;
      this.allocatedQuantity += orderAllocation;
      this.form.addControl(
        `order_allocation_${warehouse.warehouse_id}`,
        new FormControl(orderAllocation, [Validators.required, Validators.min(0), Validators.max(availableQuantity)])
      );

      
    });
  }

  initializeHandlingUnitsManagement() {
    // Initialize the handling units
    const availableHandlingUnits = this.localProduct.handling_units || [];
    this.handlingUnits = availableHandlingUnits.map((hu: any) => ({
      id: hu.id,
      type_id: hu.type_id || hu.id,
      name: hu.name,
      quantity: handlingUnitBaseMultiplier(hu),
      default: hu.default || false,
      selected: hu.selected || false,
      enabled: hu.enabled !== false,
    }));

    const defaultHandlingUnit = this.getProductHandlingUnit();
    const initialQuantity = this.getInitialQuantity(defaultHandlingUnit);
    
    const orderQuantityValidators = this.isMultiWarehouseStock()
      ? [Validators.required, Validators.min(0), Validators.max(this.getTotalAvailableQuantity() + this.reservedQuantity())]
      : (this.localProduct.is_owned
      ? [Validators.required, Validators.min(0), Validators.max(this.stock)]
      : [Validators.required, Validators.min(0)]);

    this.form.addControl(
      'order_quantity',
      new FormControl(initialQuantity, orderQuantityValidators)
    );
    this.form.addControl(
      'handling_unit',
      new FormControl(defaultHandlingUnit, [Validators.required])
    );

    this.form.get('handling_unit')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      // Look for primary warehouse and set the order_allocation_${warehouse.warehouse_id} to the getTotalUnits() and clear the other order_allocation_${warehouse.warehouse_id}
      const primaryWarehouse = this.warehouses.find((warehouse: any) => warehouse.is_primary);
      if(primaryWarehouse) {
        this.form.get('order_allocation_' + primaryWarehouse.warehouse_id)?.setValue(this.getTotalUnits(), { emitEvent: false });
        this.warehouses.forEach((warehouse: any) => {
          if(warehouse.warehouse_id !== primaryWarehouse.warehouse_id) {
            this.form.get('order_allocation_' + warehouse.warehouse_id)?.setValue(0, { emitEvent: false });
          }
        });
      }
    });
  }


  // Handle Unit Functions
  getProductHandlingUnit(): IProductHandlingUnit {
    let productHandlingUnit = this.handlingUnits[0];
    if (this.localProduct.handling_unit_id) {
      const productHU = this.handlingUnits.find(hu => hu.id === this.localProduct.handling_unit_id);
      if (productHU) {
        productHandlingUnit = productHU;
      }
    } else {
      const defaultHU = this.handlingUnits.find(hu => hu.default);
      if (defaultHU) {
        productHandlingUnit = defaultHU;
      }
    }

    return productHandlingUnit;
  }

  getInitialQuantity(productHU: IProductHandlingUnit): number {
    let initialQuantity = this.localProduct.quantity;
    if (productHU && productHU.quantity && productHU.quantity > 0) {
      if (this.localProduct.quantity < productHU.quantity) {
        initialQuantity = 1;
      } else {
        initialQuantity = Math.floor(this.localProduct.quantity / productHU.quantity);
      }
    }
    return initialQuantity;
  }

  getTotalUnits(): number {
    const activeHU = this.form.get('handling_unit')?.value || this.getProductHandlingUnit();
    const selectedHUQuantity = this.form.get('order_quantity')?.value || this.getInitialQuantity(activeHU);
    return selectedHUQuantity * handlingUnitBaseMultiplier(activeHU);
  }

  updateDiscount(event: Event) {
    const target = event.target as HTMLInputElement;
    const newValue = parseInt(target.value, 10);
    if (!isNaN(newValue) && this.localProduct.discount !== undefined) {
      this.localProduct.discount = newValue;
      this.localProduct.final_price = this.localProduct.price_unit * (1 - newValue / 100);
    }
    this.form.get('final_price')?.setValue(this.localProduct.final_price, { emitEvent: false });
  }

  updateExtraDiscount(event: Event) {
    const target = event.target as HTMLInputElement;
    const newValue = parseInt(target.value, 10);
    if (!isNaN(newValue) && this.localProduct.extra_discount !== undefined) {
      this.localProduct.extra_discount = newValue;
      let discount = this.localProduct.discount || 0;
      this.localProduct.final_price = this.localProduct.price_unit * (1 - discount / 100) * (1 - newValue / 100);
    }
    this.form.get('final_price')?.setValue(this.localProduct.final_price, { emitEvent: false });
  }

  updateFinalPrice(event: Event) {
    const target = event.target as HTMLInputElement;
    const newValue = parseFloat(target.value);
    if (!isNaN(newValue)) {
      this.localProduct.final_price = newValue;
      this.localProduct.extra_discount = 0;
      this.localProduct.discount = Number((100 - (newValue * 100) / this.localProduct.price_unit).toFixed(2));
    }
    this.form.get('extra_discount')?.setValue(this.localProduct.extra_discount, { emitEvent: false });
    this.form.get('discount')?.setValue(this.localProduct.discount, { emitEvent: false });
  }

  updateOrderAllocation() {
    let totalAllocation = 0;
    this.warehouses.forEach((warehouse: any) => {
      totalAllocation += this.form.get('order_allocation_' + warehouse.warehouse_id)?.value || 0;
    });

    
    // totalAllocation to the handling unit quantity
    const handlingUnit = this.form.get('handling_unit')?.value;
    let huQuantity = 1;
    if (handlingUnit) {
      const mult = handlingUnitBaseMultiplier(handlingUnit);
      if (totalAllocation < mult) {
        huQuantity = 1;
      } else {
        huQuantity = Math.floor(totalAllocation / mult);
      }
    }

    this.form.get('order_quantity')?.setValue(huQuantity, { emitEvent: false });
    this.allocatedQuantity = totalAllocation;
  }

  updateOrderQuantity(event: Event) {
    if (!this.isMultiWarehouseStock()) return;
    // const target = event.target as HTMLInputElement;
    // const newValue = parseInt(target.value, 10);
    const totalUnits = this.getTotalUnits();
    if (!isNaN(totalUnits)) {
      this.localProduct.quantity = totalUnits;
      this.warehouses?.forEach((warehouse: any) => {
        if(warehouse.is_primary) { 
          this.form.get('order_allocation_' + warehouse.warehouse_id)?.setValue(totalUnits, { emitEvent: false });
        } else {
          this.form.get('order_allocation_' + warehouse.warehouse_id)?.setValue(0, { emitEvent: false });
        }
      });
    }
  }

  sumAvailableQuantity(): number {
    return this.warehouses.reduce((sum, w) => sum + w.available_quantity, 0);
  }

  reservedQuantity(): number {
    if (this.isMultiWarehouseStock()) {
      return (
        this.reservations?.reduce(
          (sum: number, reservation: any) =>
            sum + (reservation.order_id == this.order.id && reservation.state == 'reserved' ? reservation.quantity : 0),
          0
        ) || 0
      );
    } else {
      return 0;
    }
  }

  

  reservedQuantityOnWarehouse(warehouseId: number): number {
    const reservedStock = (this.localProduct.reserved_stock || [])?.find((reservedStock: any) => {
      const sameStock = reservedStock.warehouse_id === warehouseId;
      const sameOrder = reservedStock.order_id === this.order.id;
      const sameState = reservedStock.state === 'reserved';
      return sameStock && sameOrder && sameState;
    });
    return reservedStock?.quantity || 0;
  }
  
  draftQuantityOnWarehouse(warehouseId: number): number {
    const draftStock = (this.localProduct.reserved_stock || [])?.find((reservedStock: any) => {
      const sameStock = reservedStock.warehouse_id === warehouseId;
      const sameOrder = reservedStock.order_id === this.order.id;
      const sameState = reservedStock.state === 'draft';
      return sameStock && sameOrder && sameState;
    });
    return draftStock?.quantity || 0;
  }

  primaryWarehouse(): IStockByWarehouse | undefined {
    return this.warehouses.find((warehouse: any) => warehouse.is_primary);
  }

  outOfStock(): boolean {
    if (this.isMultiWarehouseStock()) {
      const availableQuantity = this.sumAvailableQuantity();
      return availableQuantity < this.getTotalUnits();
    } else {
      if (this.localProduct.is_owned) {
        const killingAlert = this.lineAlerts.find(a => a.type === 'killing_stock' && a.state === 'open');
        const killingLimit = killingAlert?.details?.killing_stock_limit ?? 0;
        if (killingLimit < 0 || this.localProduct.quantity >= this.stock) return true;
      }
      return false;
    }
  }

  outOfStockMainWarehouse(): boolean {
    const mainWarehouse = this.primaryWarehouse();
    return (mainWarehouse?.available_quantity || 0) < this.getTotalUnits();
  }

  someWarehousesCantFulfill(): boolean {
    return this.warehouses.some((warehouse: any) => warehouse.available_quantity < this.form.get('order_allocation_' + warehouse.warehouse_id)?.value || 0);
  }

  warehousesCanFulfill(): boolean {
    return this.warehouses.filter((warehouse: any) => warehouse.available_quantity >= this.form.get('order_allocation_' + warehouse.warehouse_id)?.value || 0).length > 0;
  }

  hasErrorStock(): boolean {
    return this.outOfStock() || this.outOfStockMainWarehouse();
  }

  getProductGifts(): IPriceBookViewDTO[] {
    if(!this.visible || !this.product) return [];

    const gifts: IPriceBookViewDTO[] = [];

    const hasDeal = (deal: IPriceBookViewDTO) => gifts.some((d) => d.id === deal.id);

    const deal = this.product.best_deal as IPriceBookViewDTO[] | null;
    if (deal && deal.length > 0) {
      for (const d of deal) {
        if (!hasDeal(d)) {
          gifts.push(d);
        }
      }
    }

    const buyXGetYDeal = this.product.best_buy_x_get_y as IPriceBookViewDTO[] | null;
    if (buyXGetYDeal && buyXGetYDeal.length > 0) {
      for (const d of buyXGetYDeal) {
        if (!hasDeal(d)) {
          gifts.push(d);
        }
      }
    }
    
    return gifts;
  }

  close() {
    this.visible = false;

    // Wait for the next tick to ensure the form is reset
    setTimeout(() => {
      this.allocatedQuantity = 0;
      this.warehouses = [];
      this.form.reset();
      this.closed.emit();
    }, 300);
  }

  confirmAllocation() {
    if(this.confirmationInProgress) return;
    
    this.confirmationInProgress = true;
    const stockReservation = this.warehouses.map((warehouse: any) => ({
      stock_id: warehouse.stock_id,
      quantity: this.form.get('order_allocation_' + warehouse.warehouse_id)?.value,
      state: 'draft',
      warehouse_id: warehouse.warehouse_id,
      order_id: this.order.id,
      name: warehouse.name,
      id: 0,
      order_line_id: 0,
      is_primary: warehouse.is_primary,
    }));
    let stock_reservation: any[] = [];
    let reserved_stock: any[] = [];
    if (this.isMultiWarehouseStock()) {
      stock_reservation = stockReservation
        .filter((stock: any) => stock.quantity > 0)
        .map((stock: any) => ({
          stock_id: stock.stock_id,
          quantity: stock.quantity,
          state: 'draft',
          warehouse_id: stock.warehouse_id,
          order_id: stock.order_id,
          name: stock.name,
          is_primary: stock.is_primary,
          id: 0,
          order_line_id: 0,
        }));
      reserved_stock = [
        ...(this.localProduct.reserved_stock || []).filter((stock: any) => stock.order_id !== this.order.id),
        ...stockReservation
          .filter((stock: any) => stock.quantity > 0)
          .map((stock: any) => ({
            stock_id: stock.stock_id,
            quantity: stock.quantity,
            state: 'draft',
            warehouse_id: stock.warehouse_id,
            order_id: stock.order_id,
            name: stock.name,
          })),
      ];
    }
    const payload = {
      ...this.localProduct,
      discount: this.form.get('discount')?.value || this.localProduct.discount,
      extra_discount: this.form.get('extra_discount')?.value || this.localProduct.extra_discount,
      final_price: this.form.get('final_price')?.value || this.localProduct.final_price,
      quantity: this.form.get('order_quantity')?.value || this.localProduct.quantity,
      handling_unit: this.form.get('handling_unit')?.value || this.getProductHandlingUnit(),
      handling_unit_id: this.form.get('handling_unit')?.value?.id || this.localProduct.handling_unit_id,
      stock_reservation: stock_reservation,
      reserved_stock: reserved_stock,
    };

    console.log('Product Payload');
    console.log(payload);

    this.reservationChanged.emit(payload);
    this.close();
    this.confirmationInProgress = false;
  }

  getTotalAvailableQuantity(): number {
    if (this.isMultiWarehouseStock()) {
      return this.warehouses.reduce((sum, w) => sum + (w.available_quantity > 0 ? w.available_quantity : 0), 0);
    } else {
      return this.stock > 0 ? this.stock : 0;
    }
  }

  isMultiWarehouseStock(): boolean {
    return this.localProduct.stock_by_warehouse && this.localProduct.stock_by_warehouse.filter((stock: any) => stock.warehouse_id !== null).length > 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
